import React, { useState, useEffect, useMemo } from 'react';
import { Printer, Save, FileText, FileCode } from 'lucide-react';
import toast from 'react-hot-toast';
import { parseTallyXmlText } from '../utils/tallyXmlImport';
import CreditEntryReviewTable from '../components/CreditEntryReviewTable';

const FUEL_PRODUCTS = ['Petrol', 'Diesel', 'Xtragreen'];

// Standard GST state codes -> state name, derived from the GSTIN's first 2 digits
const GST_STATE_CODES = {
  '01': 'Jammu and Kashmir', '02': 'Himachal Pradesh', '03': 'Punjab', '04': 'Chandigarh',
  '05': 'Uttarakhand', '06': 'Haryana', '07': 'Delhi', '08': 'Rajasthan', '09': 'Uttar Pradesh',
  '10': 'Bihar', '11': 'Sikkim', '12': 'Arunachal Pradesh', '13': 'Nagaland', '14': 'Manipur',
  '15': 'Mizoram', '16': 'Tripura', '17': 'Meghalaya', '18': 'Assam', '19': 'West Bengal',
  '20': 'Jharkhand', '21': 'Odisha', '22': 'Chhattisgarh', '23': 'Madhya Pradesh', '24': 'Gujarat',
  '25': 'Daman and Diu', '26': 'Dadra and Nagar Haveli', '27': 'Maharashtra', '28': 'Andhra Pradesh',
  '29': 'Karnataka', '30': 'Goa', '31': 'Lakshadweep', '32': 'Kerala', '33': 'Tamil Nadu',
  '34': 'Puducherry', '35': 'Andaman and Nicobar Islands', '36': 'Telangana', '37': 'Andhra Pradesh',
  '38': 'Ladakh'
};

function placeOfSupplyFromGstin(gstin) {
  if (!gstin || gstin.length < 2) return null;
  const code = gstin.substring(0, 2);
  const state = GST_STATE_CODES[code];
  return state ? `${state} (${code})` : null;
}

// If the same vehicle got fuel AND a cash advance on the same day, fold the cash
// line into the fuel line so it prints as one row (fuel columns + Cash Amount),
// same as the paper register does.
function mergeSameVehicleCashLines(entries) {
  const groups = {};
  entries.forEach(e => {
    if (!e.vehicle_no) return;
    const key = `${e.business_date}|${e.vehicle_no}`;
    if (!groups[key]) groups[key] = [];
    groups[key].push(e);
  });

  const consumedIds = new Set();
  const merged = [];

  entries.forEach(e => {
    if (consumedIds.has(e.id)) return;
    const key = `${e.business_date}|${e.vehicle_no}`;
    const group = e.vehicle_no ? groups[key] : [e];
    const cashLines = group.filter(g => (g.item_name || '').toUpperCase() === 'CASH');
    const fuelLines = group.filter(g => (g.item_name || '').toUpperCase() !== 'CASH');

    if (cashLines.length === 1 && fuelLines.length === 1) {
      const cash = cashLines[0];
      const fuel = fuelLines[0];
      consumedIds.add(cash.id);
      consumedIds.add(fuel.id);
      if (!merged.find(m => m.id === fuel.id)) {
        merged.push({ ...fuel, cash_amount: (parseFloat(fuel.cash_amount) || 0) + (parseFloat(cash.cash_amount) || 0) });
      }
    } else {
      consumedIds.add(e.id);
      merged.push(e);
    }
  });

  return merged;
}

const Invoice = () => {
  const [creditors, setCreditors] = useState([]);
  const [party, setParty] = useState('');
  const [discountProduct, setDiscountProduct] = useState('Diesel');
  const [discountPerLitre, setDiscountPerLitre] = useState('');
  const [tfrToIocl, setTfrToIocl] = useState('');

  const [profile, setProfile] = useState({});
  const [invoiceNo, setInvoiceNo] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const [lines, setLines] = useState([]);

  useEffect(() => {
    fetch('/api/creditors', { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } })
      .then(res => res.json())
      .then(rows => { if (!rows.error) setCreditors(rows); })
      .catch(err => console.error('Failed to fetch creditors', err));

    fetch('/api/settings/profile', { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } })
      .then(res => res.json())
      .then(p => { if (!p.error) setProfile(p); })
      .catch(err => console.error('Failed to fetch pump profile', err));
  }, []);

  const handleXmlUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!party) {
      toast.error('Pick a creditor above first.');
      e.target.value = null;
      return;
    }

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const { entries, error } = await parseTallyXmlText(event.target.result, party);
        if (error) {
          toast.error(error);
          return;
        }
        setLines(entries);
        setInvoiceNo(null);
        toast.success(`Extracted ${entries.length} entries. Check them below - the invoice updates as you edit.`);
      } catch (err) {
        toast.error('Error parsing Tally XML file.');
        console.error(err);
      }
    };
    // Tally's flat XML export is UTF-16
    reader.readAsText(file, 'utf-16le');
    e.target.value = null;
  };

  const handleLineFieldChange = (id, field, value) => {
    setLines(prev => prev.map(l => (l.id === id ? { ...l, [field]: value } : l)));
  };

  const mergedLines = useMemo(() => mergeSameVehicleCashLines(lines), [lines]);

  const productTotals = useMemo(() => {
    const totals = {};
    mergedLines.forEach(e => {
      const key = e.item_name || 'Other';
      if (!totals[key]) totals[key] = { quantity: 0, amount: 0 };
      totals[key].quantity += parseFloat(e.quantity) || 0;
      totals[key].amount += parseFloat(e.amount) || 0;
    });
    return totals;
  }, [mergedLines]);

  const totalAmount = useMemo(() => mergedLines.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0), [mergedLines]);
  const totalCash = useMemo(() => mergedLines.reduce((s, e) => s + (parseFloat(e.cash_amount) || 0), 0), [mergedLines]);

  const dateRange = useMemo(() => {
    const dates = mergedLines.map(e => e.business_date).filter(Boolean).sort();
    return dates.length > 0 ? { from: dates[0], to: dates[dates.length - 1] } : null;
  }, [mergedLines]);

  const totals = useMemo(() => {
    const discountQty = productTotals[discountProduct]?.quantity || 0;
    const discountRate = parseFloat(discountPerLitre) || 0;
    const discountAmount = discountQty * discountRate;
    const tfr = parseFloat(tfrToIocl) || 0;
    const total = totalAmount + totalCash;
    const grandTotal = total - discountAmount - tfr;
    return { discountQty, discountAmount, tfr, total, grandTotal };
  }, [productTotals, discountProduct, discountPerLitre, tfrToIocl, totalAmount, totalCash]);

  const handleGenerateInvoiceNo = async () => {
    if (mergedLines.length === 0 || !dateRange) return;
    setIsSaving(true);
    try {
      const res = await fetch('/api/invoices', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({
          party_name: party,
          date_from: dateRange.from,
          date_to: dateRange.to,
          discount_per_litre: parseFloat(discountPerLitre) || 0,
          tfr_to_iocl: totals.tfr,
          total_amount: totalAmount,
          cash_amount: totalCash,
          discount_amount: totals.discountAmount,
          grand_total: totals.grandTotal
        })
      });
      const json = await res.json();
      if (json.status === 'success') {
        setInvoiceNo(json.invoice_no);
        toast.success(`Invoice ${json.invoice_no} generated`);
      } else {
        toast.error(json.error || 'Failed to save invoice');
      }
    } catch (err) {
      toast.error(err.message);
    }
    setIsSaving(false);
  };

  const fmt = (v) => Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtDate = (d) => {
    const dt = new Date(d);
    return `${String(dt.getDate()).padStart(2, '0')}-${String(dt.getMonth() + 1).padStart(2, '0')}-${dt.getFullYear()}`;
  };

  const productGroups = useMemo(() => (
    FUEL_PRODUCTS.filter(p => productTotals[p]).map(p => ({
      name: p,
      rows: mergedLines.filter(e => e.item_name === p)
    }))
  ), [mergedLines, productTotals]);

  const cashRows = useMemo(() => mergedLines.filter(e => e.item_name === 'CASH'), [mergedLines]);

  const placeOfSupply = placeOfSupplyFromGstin(profile.gstin);
  const hasInvoice = mergedLines.length > 0 && dateRange;

  return (
    <div className="max-w-5xl mx-auto pb-16">
      {/* Controls - hidden when printing */}
      <div className="no-print space-y-4 mb-6 glass-panel p-6">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <FileText className="w-6 h-6 text-indigo-600" /> Invoice Generator
        </h1>
        <p className="text-sm text-slate-500 -mt-2">Import a Tally XML for a creditor, check the lines, and generate the printable invoice - nothing here is pulled from past records.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Creditor (Billed to)</label>
            <select className="input-field" value={party} onChange={e => { setParty(e.target.value); setLines([]); setInvoiceNo(null); }}>
              <option value="">Select creditor...</option>
              {creditors.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Discount applies to</label>
            <select className="input-field" value={discountProduct} onChange={e => setDiscountProduct(e.target.value)}>
              {FUEL_PRODUCTS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Discount per Litre (₹)</label>
            <input type="number" step="0.01" className="input-field" placeholder="e.g. 0.50" value={discountPerLitre} onChange={e => setDiscountPerLitre(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">TFR to IOCL (₹)</label>
            <input type="number" className="input-field" placeholder="0" value={tfrToIocl} onChange={e => setTfrToIocl(e.target.value)} />
          </div>
        </div>
        <div className="flex gap-3 flex-wrap">
          <label className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-sm shadow-sm ${party ? 'bg-indigo-600 text-white cursor-pointer hover:bg-indigo-700' : 'bg-slate-100 text-slate-400 cursor-not-allowed'}`}>
            <FileCode className="w-4 h-4" />
            Import Tally XML
            <input type="file" accept=".xml" className="hidden" disabled={!party} onChange={handleXmlUpload} />
          </label>
          <button onClick={handleGenerateInvoiceNo} disabled={!hasInvoice || isSaving} className="btn-primary flex items-center gap-2 px-4 py-2">
            <Save className="w-4 h-4" /> {isSaving ? 'Saving...' : (invoiceNo ? 'Re-save Invoice No.' : 'Assign Invoice No.')}
          </button>
          <button onClick={() => window.print()} disabled={!hasInvoice} className="flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 font-medium text-sm">
            <Printer className="w-4 h-4" /> Print / Save as PDF
          </button>
        </div>
      </div>

      {lines.length > 0 && (
        <div className="no-print bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden mb-6">
          <div className="p-5 border-b border-slate-100 bg-indigo-50/50">
            <h2 className="font-bold text-indigo-900">Check these - the invoice below updates as you edit</h2>
          </div>
          <CreditEntryReviewTable
            entries={lines}
            onChangeField={handleLineFieldChange}
            onDelete={(id) => setLines(prev => prev.filter(e => e.id !== id))}
          />
        </div>
      )}

      {hasInvoice && (
        <div className="bg-white border-2 border-slate-800 p-6 print-invoice">
          <div className="flex justify-between items-start border-b-2 border-slate-800 pb-2 mb-2">
            <div className="text-xs font-mono">GSTIN : {profile.gstin || '-'}</div>
            <div className="text-sm font-bold underline">Original Copy</div>
          </div>

          <div className="text-center mb-3">
            <div className="text-lg font-bold underline mb-2">TAX INVOICE</div>
            <div className="flex items-center justify-center gap-3">
              <img src="/assets/indianoil-logo.png" alt="Indian Oil" className="w-14 h-14" />
              <h1 className="text-3xl font-extrabold uppercase">{profile.pump_name || 'Pump Name'}</h1>
            </div>
            <p className="text-sm mt-1">{profile.address}</p>
            <p className="text-sm font-semibold underline">
              Tel. : {profile.phone || '-'} &nbsp;&nbsp;&nbsp; {profile.email || ''}
            </p>
          </div>

          <div className="border-2 border-slate-800 text-center py-2 mb-2">
            <div className="font-bold underline">Bank Detail</div>
            <div className="text-sm">Bank Name: {profile.bank_name || '-'}</div>
            <div className="text-sm">Account No: {profile.bank_account || '-'}</div>
            <div className="text-sm">IFSC Code: {profile.bank_ifsc || '-'}</div>
          </div>

          <div className="invoice-meta-block grid grid-cols-2 border-2 border-slate-800 text-sm">
            <div className="p-2 border-r border-b border-slate-800"><b>Invoice No.</b> : {invoiceNo || '(not yet saved)'}</div>
            <div className="p-2 border-b border-slate-800"><b>Place of Supply</b> : {placeOfSupply || '-'}</div>
            <div className="p-2 border-r border-b border-slate-800"><b>Date of Invoice</b> : {fmtDate(dateRange.from)} To- {fmtDate(dateRange.to)}</div>
            <div className="p-2 border-b border-slate-800"><b>Reverse Charges</b> : NA</div>
            <div className="p-2 border-r col-span-1"><b>Billed to</b> :- {party}</div>
            <div className="p-2"><b>Shipped to</b> :-</div>
          </div>

          {productGroups.map(group => (
            <div key={group.name} className="mt-4">
              <div className="text-center text-xl font-bold border-2 border-b-0 border-slate-800 py-1">{group.name === 'Diesel' ? 'HSD' : group.name === 'Petrol' ? 'MS' : group.name}</div>
              <table className="w-full border-collapse border-2 border-slate-800 text-sm">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="border border-slate-800 p-1">S.No.</th>
                    <th className="border border-slate-800 p-1">Date</th>
                    <th className="border border-slate-800 p-1">Vehicle No.</th>
                    <th className="border border-slate-800 p-1">Voucher No.</th>
                    <th className="border border-slate-800 p-1">Quantity</th>
                    <th className="border border-slate-800 p-1">Rate</th>
                    <th className="border border-slate-800 p-1">Amount</th>
                    <th className="border border-slate-800 p-1">Cash Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {group.rows.map((r, idx) => (
                    <tr key={r.id}>
                      <td className="border border-slate-800 p-1 text-center">{idx + 1}</td>
                      <td className="border border-slate-800 p-1 text-center">{fmtDate(r.business_date)}</td>
                      <td className="border border-slate-800 p-1 text-center">{r.vehicle_no || '-'}</td>
                      <td className="border border-slate-800 p-1 text-center">{r.voucher_no || '-'}</td>
                      <td className="border border-slate-800 p-1 text-right">{Number(r.quantity || 0).toFixed(2)}</td>
                      <td className="border border-slate-800 p-1 text-right">{Number(r.rate || 0).toFixed(2)}</td>
                      <td className="border border-slate-800 p-1 text-right">{Math.round(r.amount || 0)}</td>
                      <td className="border border-slate-800 p-1 text-right">{Number(r.cash_amount || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                  <tr className="font-bold">
                    <td colSpan={4} className="border border-slate-800 p-1 text-right">TOTAL {group.name === 'Diesel' ? 'HSD' : group.name.toUpperCase()} :-</td>
                    <td className="border border-slate-800 p-1 text-right">{productTotals[group.name].quantity.toFixed(2)}</td>
                    <td className="border border-slate-800 p-1"></td>
                    <td className="border border-slate-800 p-1"></td>
                    <td className="border border-slate-800 p-1"></td>
                  </tr>
                </tbody>
              </table>
            </div>
          ))}

          {cashRows.length > 0 && (
            <div className="mt-4">
              <div className="text-center text-xl font-bold border-2 border-b-0 border-slate-800 py-1">CASH GIVEN TO DRIVER</div>
              <table className="w-full border-collapse border-2 border-slate-800 text-sm">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="border border-slate-800 p-1">S.No.</th>
                    <th className="border border-slate-800 p-1">Date</th>
                    <th className="border border-slate-800 p-1">Vehicle No.</th>
                    <th className="border border-slate-800 p-1">Voucher No.</th>
                    <th className="border border-slate-800 p-1">Cash Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {cashRows.map((r, idx) => (
                    <tr key={r.id}>
                      <td className="border border-slate-800 p-1 text-center">{idx + 1}</td>
                      <td className="border border-slate-800 p-1 text-center">{fmtDate(r.business_date)}</td>
                      <td className="border border-slate-800 p-1 text-center">{r.vehicle_no || '-'}</td>
                      <td className="border border-slate-800 p-1 text-center">{r.voucher_no || '-'}</td>
                      <td className="border border-slate-800 p-1 text-right">{Number(r.cash_amount || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="invoice-footer-block">
            <div className="mt-4 flex justify-between items-start gap-6">
              <div className="text-xs text-slate-500 max-w-xs">
                This is a computer generated tax invoice.
              </div>
              <table className="border-collapse border-2 border-slate-800 text-sm min-w-[320px]">
                <tbody>
                  <tr><td className="border border-slate-800 p-1 font-bold">{discountProduct === 'Diesel' ? 'HSD' : discountProduct.toUpperCase()} AMOUNT</td><td className="border border-slate-800 p-1 text-right">{fmt(totalAmount)}</td></tr>
                  <tr><td className="border border-slate-800 p-1 font-bold">CASH AMOUNT</td><td className="border border-slate-800 p-1 text-right">{fmt(totalCash)}</td></tr>
                  <tr><td className="border border-slate-800 p-1 font-bold">TOTAL</td><td className="border border-slate-800 p-1 text-right">{fmt(totals.total)}</td></tr>
                  <tr><td className="border border-slate-800 p-1 font-bold">DISCOUNT @{parseFloat(discountPerLitre || 0).toFixed(2)}</td><td className="border border-slate-800 p-1 text-right">{fmt(totals.discountAmount)}</td></tr>
                  <tr><td className="border border-slate-800 p-1 font-bold">TFR TO IOCL</td><td className="border border-slate-800 p-1 text-right">{fmt(totals.tfr)}</td></tr>
                  <tr className="text-base"><td className="border border-slate-800 p-1 font-extrabold">G.TOTAL</td><td className="border border-slate-800 p-1 text-right font-extrabold">{fmt(totals.grandTotal)}</td></tr>
                </tbody>
              </table>
            </div>

            <div className="mt-10 flex justify-end">
              <div className="text-center">
                <p className="font-bold">{profile.pump_name}</p>
                <p className="mt-8">Manager</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @media print {
          .no-print, nav, aside, header { display: none !important; }
          .print-invoice { border: none !important; }
          body { background: white; }
          /* The app shell wraps pages in a fixed-height, scrollable <main> (h-screen +
             overflow-y-auto) for normal browsing. Printing doesn't paginate scrollable
             overflow - it just clips to whatever fits in that box - so a long invoice
             gets silently cut off (e.g. the G.Total row and signature never appear).
             Release those constraints here so the full page can flow across print pages. */
          html, body, #root { height: auto !important; overflow: visible !important; }
          .h-screen { height: auto !important; }
          .overflow-hidden, .overflow-y-auto, .overflow-x-hidden { overflow: visible !important; }

          /* Without this, a table row (or a small block like the totals table) can get
             sliced in half wherever the page happens to end - e.g. an amount's number
             printing on page 1 while the rest of that row lands on page 2. Keep each
             row, and the small footer/meta blocks, as one unbreakable unit instead. */
          .print-invoice tr { break-inside: avoid; page-break-inside: avoid; }
          .print-invoice thead { display: table-header-group; }
          .invoice-meta-block, .invoice-footer-block {
            break-inside: avoid; page-break-inside: avoid;
          }
        }
      `}</style>
    </div>
  );
};

export default Invoice;
